import React, { useEffect, useState } from 'react';
import { Bookmark, Eye, Loader2, MessageSquare, Send, Sparkles } from 'lucide-react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import { AiResultPanels, AiResultSkeleton } from './AiResultPanels';
import type { AiResultMap, RccCandidate, RccHandlers, RccJobColumn, TFn } from './rccTypes';

export function RccCandidateExpand({
  candidate: c,
  col,
  t,
  h,
  aiResults,
  aiLoading,
  companyId,
  renderAtsButton,
}: {
  candidate: RccCandidate;
  col: RccJobColumn;
  t: TFn;
  h: RccHandlers;
  aiResults: AiResultMap;
  aiLoading: Record<string, boolean>;
  companyId: string | null;
  renderAtsButton: (candidate: RccCandidate) => React.ReactNode;
}) {
  const jobTitle = col.title;
  const aiResult = aiResults[`${c.cv_id}__${jobTitle}`];
  const saved = h.getJobSaves(c.cv_id).includes(jobTitle);
  const [insightsOpen, setInsightsOpen] = useState(false);

  // Auto-reveal insights as soon as results arrive (e.g. after analysis finishes).
  useEffect(() => {
    if (aiResult) setInsightsOpen(true);
  }, [aiResult]);

  return (
    <div className="space-y-2.5">
      {c.role_preferences && (
        <p className="text-[11px] leading-relaxed text-slate-600">
          <span className="font-medium text-slate-500">{t('card.seeking')} · </span>
          {c.role_preferences}
        </p>
      )}

      <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-1">
          <Button variant="outline" size="xs" onClick={() => h.openCvModal(c.cv_id)}>
            <Eye aria-hidden />
            {t('card.cv')}
          </Button>

          {!aiResult && (
            <Button
              variant="outline"
              size="xs"
              disabled={!!aiLoading[c.cv_id]}
              onClick={() => h.generateAI(c, jobTitle, col)}
            >
              {aiLoading[c.cv_id] ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : (
                <Sparkles className="text-pink-500" aria-hidden />
              )}
              {aiLoading[c.cv_id] ? t('card.ai_analyzing') : t('card.ai')}
            </Button>
          )}

          {aiResult && (
            <Button
              variant="outline"
              size="xs"
              aria-expanded={insightsOpen}
              onClick={() => setInsightsOpen((v) => !v)}
            >
              <Sparkles className="text-pink-500" aria-hidden />
              {t('card.ai')}
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1 sm:justify-end">
          {!c.reach_out_id ? (
            <Button
              variant="outline"
              size="xs"
              onClick={() => h.openReachOut(c, jobTitle)}
              className="border-pink-200 bg-pink-50 text-pink-600 hover:border-pink-300 hover:bg-pink-100 hover:text-pink-700"
            >
              <Send aria-hidden />
              {t('card.contact')}
            </Button>
          ) : c.conversation_id ? (
            <Button
              variant="outline"
              size="xs"
              onClick={() => h.openChat(c.conversation_id!)}
              className="border-transparent bg-transparent text-emerald-600 hover:border-emerald-100 hover:bg-emerald-50 hover:text-emerald-700"
            >
              <MessageSquare aria-hidden />
              {t('card.chat')}
            </Button>
          ) : null}

          {companyId && c.explorer_id && renderAtsButton(c)}

          <button
            type="button"
            onClick={() => h.toggleSave(c.cv_id, jobTitle)}
            aria-label={saved ? t('card.saved') : t('card.save')}
            aria-pressed={saved}
            className={cn(
              'inline-flex size-6 shrink-0 items-center justify-center rounded-md border transition-colors',
              saved
                ? 'border-pink-500 bg-pink-500 text-white hover:bg-pink-600 hover:border-pink-600'
                : 'border-slate-200 bg-white text-slate-400 hover:border-slate-300 hover:text-slate-600',
            )}
          >
            <Bookmark className={cn('size-3.5', saved && 'fill-current')} />
          </button>
        </div>
      </div>

      {aiLoading[c.cv_id] && !aiResult && <AiResultSkeleton t={t} />}

      {aiResult && insightsOpen && (
        <AiResultPanels summary={aiResult.summary} fit={aiResult.fit} t={t} />
      )}
    </div>
  );
}
